import React, { Profiler, useCallback, useState, useRef } from "react";
import {
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  theme,
  ThemeProvider
} from "@chakra-ui/core";
import { Slider as Slider4 } from "@material-ui/core";
import Slider5 from "@material-ui/lab/SliderStyled";
import UnstyledSlider from "./UnstyledSlider";
import "antd/dist/antd.css";
import { Slider as AntdSlider } from "antd";
import queryString from "query-string";
import "./styles.css";

const qs = queryString.parse(window.location.search, { arrayFormat: "comma" });

const NUM_COMPONENTS = Number(qs.components) || 1;
const TEST_CASES = qs.cases || [
  "chakra",
  "antd",
  "mui-v4",
  "mui-v5",
  "mui-v5-unstyled",
  "native"
];

const last = (arr) => arr[arr.length - 1];
const mean = (arr) =>
  arr.reduce((sum, item) => sum + item, 0) / (arr.length || 1);
const round = (value = 0) => Math.round(value * 100) / 100;

const data = {
  name: "Frozen yoghurt",
  calories: 159,
  fat: 6.0,
  carbs: 24,
  protein: 4.0
};
const rows = Array.from(new Array(100)).map(() => data);

function ChakraTest() {
  return (
    <div className="test-case-container">
      {rows.map((row, index) => (
        <Slider key={index} defaultValue={30}>
          <SliderTrack />
          <SliderFilledTrack />
          <SliderThumb />
        </Slider>
      ))}
    </div>
  );
}

function Mui4TestCase() {
  return (
    <div className="test-case-container">
      {rows.map((row, index) => (
        <Slider4 key={index} defaultValue={30} />
      ))}
    </div>
  );
}

function Mui5TestCase() {
  return (
    <div className="test-case-container">
      {rows.map((row, index) => (
        <Slider5 key={index} defaultValue={30} />
      ))}
    </div>
  );
}

function Mui5UnstyledTestCase() {
  return (
    <div className="test-case-container">
      {rows.map((row, index) => (
        <UnstyledSlider key={index} defaultValue={30} />
      ))}
    </div>
  );
}

function AntdTestCase() {
  return (
    <div className="test-case-container">
      {rows.map((row, index) => (
        <AntdSlider key={index} defaultValue={30} />
      ))}
    </div>
  );
}

function NativeTestCase() {
  return (
    <div className="test-case-container">
      {rows.map((row, index) => (
        <input key={index} type="range" defaultValue={30} />
      ))}
    </div>
  );
}

const timings = {};

function TestCase(props) {
  const { Component, name } = props;
  const timeElementRef = useRef();

  const onRender = useCallback(
    (
      id, // the "id" prop of the Profiler tree that has just committed
      phase, // either "mount" (if the tree just mounted) or "update" (if it re-rendered)
      actualDuration, // time spent rendering the committed update
      baseDuration, // estimated time to render the entire subtree without memoization
      startTime, // when React began rendering this update
      commitTime, // when React committed this update
      interactions // the Set of interactions belonging to this update
    ) => {
      if (!timings[name]) {
        timings[name] = { mount: [], rerender: [] };
      }
      const thisTimings = timings[name];

      if (phase === "mount") {
        thisTimings.mount.push(actualDuration);
      } else {
        thisTimings.rerender.push(actualDuration);
      }

      const lastMount = round(last(thisTimings.mount));
      const lastRerender = round(last(thisTimings.rerender));
      const meanMount = round(mean(thisTimings.mount));
      const meanRerender = round(mean(thisTimings.rerender));

      timeElementRef.current.textContent = `
Last mount: ${lastMount}ms
Mean mount: ${meanMount}ms
Last rerender: ${lastRerender}ms
Mean rerender: ${meanRerender}ms

`;
    },
    [name]
  );

  return (
    <Profiler id={name} onRender={onRender}>
      <div className="test-case">
        <div className="name">{name}</div>
        <div className="time" ref={timeElementRef}></div>
        <Component />
      </div>
    </Profiler>
  );
}

function TestSuite() {
  return (
    <div className="suite">
      <div>
        {TEST_CASES.includes("native") && (
          <TestCase name="Native slider" Component={NativeTestCase} />
        )}
        {TEST_CASES.includes("mui-v5-unstyled") && (
          <TestCase
            name="Material-UI v5 unstyled slider"
            Component={Mui5UnstyledTestCase}
          />
        )}
        {TEST_CASES.includes("mui-v4") && (
          <TestCase name="Material-UI v4 slider" Component={Mui4TestCase} />
        )}
        {TEST_CASES.includes("mui-v5") && (
          <TestCase name="Material-UI v5 slider" Component={Mui5TestCase} />
        )}
        {TEST_CASES.includes("antd") && (
          <TestCase name="Antd slider" Component={AntdTestCase} />
        )}
        {TEST_CASES.includes("chakra") && (
          <TestCase name="Chakra slider" Component={ChakraTest} />
        )}
      </div>
    </div>
  );
}

export default function App() {
  const [runCount, setRunCount] = useState(0);
  const onClickRun = useCallback(() => {
    let nextRunCount = runCount + 1;
    setRunCount(-1);
    setTimeout(() => {
      setRunCount(nextRunCount);
    }, 50);
  }, [runCount]);

  const onClickRerender = useCallback(() => {
    let nextRunCount = runCount + 1;
    setRunCount(nextRunCount);
  }, [runCount]);

  return (
    <div className="App">
      <h1>Slider Perf Comparison</h1>
      <p>Click the button below to begin.</p>
      <button onClick={onClickRun} disabled={runCount === -1}>
        {runCount > 0 ? "Run Again" : "Run Test Suite"}
      </button>
      <button
        onClick={onClickRerender}
        disabled={runCount <= 0}
        style={{ marginLeft: 10 }}
      >
        Rerender
      </button>
      <br />
      <br />
      <ThemeProvider theme={theme}>
        {runCount > 0 && <TestSuite />}
      </ThemeProvider>
    </div>
  );
}
